
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  Input, OnChanges, OnInit, Output, SimpleChanges, EventEmitter
} from '@angular/core';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/* This component depends on a 3rd part package: marked to compile markdown to html */
import { marked } from 'marked';

@Component({
  selector: 'rappider-markdown-viewer',
  templateUrl: 'markdown-viewer.component.html',
  styleUrls: ['./markdown-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RappiderMarkdownViewerComponent implements OnInit, OnChanges {
  @Input() markdownText: string | undefined;
  @Input() typeInRealTime? = false;
  @Input() customMarkdownClass?: string | undefined;
  @Input() typingSpeed? = 1; // milliseconds, less is faster

  @Output() realtimeTypingCompleted = new EventEmitter<string>();
  /* Every x ms, the component emits typingInProgress so that parent component can update chat scrolling etc  */
  @Output() typingInProgress = new EventEmitter<string>();

  /* This component doesnt use the input directly to manage the local state */
  _markdownText = '';
  markDownPreviewValue: SafeHtml = '';
  _typingIndex = 0;

  /* The string variable that saves the types text if typeInRealTime is true  */
  typingText = '';

  constructor(private cdr: ChangeDetectorRef, private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this._markdownText = this.markdownText;
    if (!this.typeInRealTime) {
      this.setMarkDownPreviewValue(this._markdownText);
    } else {
      this.typeTextInRealTime();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.markdownText?.currentValue && changes?.markdownText?.currentValue !== this._markdownText) {
      this.typingText = '';
      this._typingIndex = 0;
      this._markdownText = changes.markdownText?.currentValue;
      this.setMarkDownPreviewValue(this._markdownText);
    }
  }

  async setMarkDownPreviewValue(markdownTextToDisplay: string) {
    // Set global marked options
    marked.setOptions({
      gfm: true,
      breaks: true, // this is important to keep \n as <br>
    });

    const parsedHtml = await Promise.resolve(marked(markdownTextToDisplay ?? ''));

    this.markDownPreviewValue = this.sanitizer.bypassSecurityTrustHtml(parsedHtml);
    this.cdr.detectChanges();
  }


  typeTextInRealTime(charsPerStep: number = 15): void {
    let typingInterval: any;

    const startTypingProgressEmitter = () => {
      typingInterval = setInterval(() => {
        this.typingInProgress.emit(new Date().toISOString());
      }, 500);
    };

    const stopTypingProgressEmitter = () => {
      if (typingInterval) {
        clearInterval(typingInterval);
        typingInterval = null;
      }
    };

    const typeChar = () => {
      if (this._typingIndex < this._markdownText?.length) {
        // Add multiple characters at once based on charsPerStep
        const nextChunk = this._markdownText.substr(this._typingIndex, charsPerStep);
        this.typingText += nextChunk;

        this.setMarkDownPreviewValue(this.typingText);

        this._typingIndex += charsPerStep;
        this.cdr.detectChanges(); // Update UI

        setTimeout(typeChar, this.typingSpeed);
      } else {
        stopTypingProgressEmitter();
        this.realtimeTypingCompleted.emit(new Date().toISOString());
      }
    };

    startTypingProgressEmitter(); // Start emitting every 500ms
    typeChar(); // Start typing
  }


}
